import type { UserAttributes } from '@optimizely/optimizely-sdk';
import type { SignedInUser, User } from '@core/auth/models/user';
import { getUser } from '@core/auth/redux';
import { toLocale } from '@core/internationalize/models/locale';
import { useReduxState } from '@engage/redux/hooks';
import { isOk } from '@engage/result/pendingResult';
import { isProdEnv } from '@members/env';
import { StorageKeys, syncWithQueryParam } from '@members/env/localStorageEnv';
import type { experiments } from '@members/feature-toggles/toggles/featureToggles.generated';
import { useClient } from './client';
import { getAjsUserId } from './utils';

export type ExperimentFeature = keyof typeof experiments;
export type ExperimentVariation =
  typeof experiments[ExperimentFeature][number]['variations'][number];
export type ExperimentKey = typeof experiments[ExperimentFeature][number]['key'];

const getMemberGroup = (user?: User) =>
  !!user
    ? (user as SignedInUser)?.memberGroups?.reduce(
        (acc, elem) => ({
          ...acc,
          [elem]: true,
        }),
        {} as Record<string, boolean>,
      )
    : {};

export const useExperimentVariation = (
  experimentKey: ExperimentKey,
  attributes: UserAttributes = {},
) => {
  const user = useReduxState(getUser);
  const memberGroups = getMemberGroup(user);

  const locale = toLocale();

  const userId = getAjsUserId();
  const [client] = useClient();
  const localOverride = syncWithQueryParam(StorageKeys.FeatureExperiments);
  const experimentLocalStorage = !!localOverride && JSON.parse(localOverride);
  let overrideValue, originalValue;
  if (!isProdEnv() && experimentLocalStorage?.[experimentKey]) {
    overrideValue = experimentLocalStorage?.[experimentKey];
  }
  if (userId && isOk(client)) {
    originalValue = client.value.getVariation(experimentKey, userId, {
      ...memberGroups,
      locale,
      ...attributes,
    });
  }
  return {
    originalValue: originalValue as ExperimentVariation,
    variationName: (overrideValue ?? originalValue) as ExperimentVariation,
  };
};
